import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import AccessTimeRoundedIcon from "@mui/icons-material/AccessTimeRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { zodResolver } from "@hookform/resolvers/zod";
import { asMoney, asRatePercent, formatRatePercent } from "@bw/shared/types";
import { formatMoney } from "@bw/shared/utils";
import { useEffect, useState, type ReactNode } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  fromZonedDateTimeInput,
  toZonedDateTimeInput,
} from "@/libs/zonedDateTime";
import {
  campaignServiceContext,
  type CampaignAuthoringContext,
  type CampaignCreate,
  type CampaignResponse,
} from "@/libs/campaignsApi";

type Goal =
  | "welcome"
  | "service_welcome"
  | "quiet_hours"
  | "limited_time"
  | "repeat_visits"
  | "spend_save"
  | "win_back"
  | "referral"
  | "loyalty"
  | "service";
type Step = "goal" | "service" | "details" | "review";
type PromotionService = CampaignCreate["service"];

const servicePresentation: Record<
  PromotionService,
  { label: string; noun: string; defaultName: string; setupName: string }
> = {
  court_reservation: {
    label: "Court reservations",
    noun: "court reservation",
    defaultName: "Court reservation special",
    setupName: "court reservations",
  },
  open_play: {
    label: "Drop-in",
    noun: "drop-in visit",
    defaultName: "Drop-in special",
    setupName: "drop-in",
  },
  classes: {
    label: "Classes & clinics",
    noun: "class or clinic enrollment",
    defaultName: "Class and clinic special",
    setupName: "a published class",
  },
  coaching: {
    label: "Private coaching",
    noun: "private coaching agreement",
    defaultName: "Private coaching special",
    setupName: "published coaching and lesson pricing",
  },
};

const amount = z
  .string()
  .trim()
  .regex(/^\d+(\.\d{1,2})?$/, "Use an amount with up to two decimals.");
const optionalAmount = z.union([z.literal(""), amount]);
const weekdays = [
  ["monday", "Mon"],
  ["tuesday", "Tue"],
  ["wednesday", "Wed"],
  ["thursday", "Thu"],
  ["friday", "Fri"],
  ["saturday", "Sat"],
  ["sunday", "Sun"],
] as const;
const weekday = z.enum([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);
const detailsSchema = z
  .object({
    name: z.string().trim().min(1, "Name this promotion.").max(255),
    discountType: z.enum(["percentage", "fixed_amount"]),
    discountPercent: optionalAmount,
    discountAmount: optionalAmount,
    spendCap: amount.refine(
      (value) => Number(value) > 0,
      "Set a budget above zero.",
    ),
    minimumCart: optionalAmount,
    maximumDiscount: optionalAmount,
    perPlayerLimit: z.number().int().min(1).max(100),
    inactiveForDays: z.number().int().min(30).max(365),
    rewardThreshold: z.number().int().min(2).max(50),
    rewardValidForDays: z.number().int().min(7).max(365),
    eligibleWeekdays: z.array(weekday),
    startsAt: z.string().min(1, "Choose a start time."),
    endsAt: z.string().min(1, "Choose an end time."),
    localTimeStart: z.string(),
    localTimeEnd: z.string(),
  })
  .superRefine((value, ctx) => {
    if (
      value.discountType === "percentage" &&
      (!value.discountPercent ||
        Number(value.discountPercent) <= 0 ||
        Number(value.discountPercent) > 100)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["discountPercent"],
        message: "Enter 1–100%.",
      });
    }
    if (
      value.discountType === "fixed_amount" &&
      (!value.discountAmount || Number(value.discountAmount) <= 0)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["discountAmount"],
        message: "Enter an amount above zero.",
      });
    }
    if (
      value.discountType === "fixed_amount" &&
      value.discountAmount &&
      value.minimumCart &&
      Number(value.discountAmount) >= Number(value.minimumCart)
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["discountAmount"],
        message: "Savings must be less than the minimum purchase.",
      });
    }
    if (value.localTimeStart && !value.localTimeEnd) {
      ctx.addIssue({
        code: "custom",
        path: ["localTimeEnd"],
        message: "Choose an end time.",
      });
    }
    if (value.localTimeEnd && !value.localTimeStart) {
      ctx.addIssue({
        code: "custom",
        path: ["localTimeStart"],
        message: "Choose a start time.",
      });
    }
    if (
      value.localTimeStart &&
      value.localTimeEnd &&
      value.localTimeEnd <= value.localTimeStart
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["localTimeEnd"],
        message: "Eligible until must be after Eligible from.",
      });
    }
  });

type Details = z.infer<typeof detailsSchema>;

const defaultDetails: Details = {
  name: "",
  discountType: "percentage",
  discountPercent: "20",
  discountAmount: "",
  spendCap: "500",
  minimumCart: "",
  maximumDiscount: "15",
  perPlayerLimit: 1,
  inactiveForDays: 60,
  rewardThreshold: 5,
  rewardValidForDays: 30,
  eligibleWeekdays: [],
  startsAt: "",
  endsAt: "",
  localTimeStart: "",
  localTimeEnd: "",
};

const goals: Array<{
  id: Goal;
  title: string;
  description: string;
  icon: ReactNode;
}> = [
  {
    id: "welcome",
    title: "Welcome new players",
    description: "Convert someone who has never completed a paid booking here.",
    icon: <PersonAddAltRoundedIcon />,
  },
  {
    id: "service_welcome",
    title: "Welcome first-time service customers",
    description:
      "Reward someone using this specific service for the first time.",
    icon: <PersonAddAltRoundedIcon />,
  },
  {
    id: "quiet_hours",
    title: "Fill quiet hours",
    description: "Offer selected weekday times when you have spare capacity.",
    icon: <AccessTimeRoundedIcon />,
  },
  {
    id: "limited_time",
    title: "Run a limited-time special",
    description: "Create urgency with an offer that ends on a clear date.",
    icon: <AccessTimeRoundedIcon />,
  },
  {
    id: "repeat_visits",
    title: "Reward repeat visits",
    description: "Let each player redeem the same offer a few times.",
    icon: <CampaignRoundedIcon />,
  },
  {
    id: "spend_save",
    title: "Spend more, save more",
    description: "Offer a fixed saving when a purchase reaches your threshold.",
    icon: <CampaignRoundedIcon />,
  },
  {
    id: "win_back",
    title: "Bring players back",
    description:
      "Offer a verified former customer a reason to return after time away.",
    icon: <CampaignRoundedIcon />,
  },
  {
    id: "referral",
    title: "Refer a friend",
    description:
      "Reward both players only after the friend completes a verified visit.",
    icon: <PersonAddAltRoundedIcon />,
  },
  {
    id: "loyalty",
    title: "Earn a visit reward",
    description:
      "Issue a one-time reward after a player completes a verified service milestone.",
    icon: <CampaignRoundedIcon />,
  },
  {
    id: "service",
    title: "Build a custom offer",
    description: "Choose your own audience, value, usage limit, and schedule.",
    icon: <CampaignRoundedIcon />,
  },
];

const services: PromotionService[] = [
  "court_reservation",
  "open_play",
  "classes",
  "coaching",
];

export function PromotionStudioDialog({
  open,
  context,
  initialCampaign,
  busy,
  onClose,
  onCreate,
}: {
  open: boolean;
  context: CampaignAuthoringContext;
  initialCampaign?: CampaignResponse | null;
  busy: boolean;
  onClose: () => void;
  onCreate: (body: CampaignCreate) => Promise<CampaignResponse>;
}) {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("sm"));
  const [step, setStep] = useState<Step>("goal");
  const [goal, setGoal] = useState<Goal | null>(null);
  const [selectedService, setSelectedService] =
    useState<PromotionService | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<CampaignCreate | null>(null);
  const form = useForm<Details>({
    resolver: zodResolver(detailsSchema),
    defaultValues: defaultDetails,
  });
  const discountType = form.watch("discountType");
  const discountPercent = form.watch("discountPercent");
  const discountAmount = form.watch("discountAmount");
  const audience =
    goal === "welcome"
      ? "new_to_facility"
      : goal === "service_welcome"
        ? "new_to_service"
        : goal === "win_back"
          ? "lapsed_to_service"
          : goal === "referral"
            ? "earned_referral"
            : goal === "loyalty"
              ? "earned_loyalty"
              : "all_players";
  const inactiveForDays = form.watch("inactiveForDays");
  const serviceContext = selectedService
    ? campaignServiceContext(context, selectedService)
    : undefined;
  const currency = serviceContext?.currency?.toUpperCase() ?? null;
  const serviceCopy = selectedService
    ? servicePresentation[selectedService]
    : null;

  useEffect(() => {
    if (!open) return;
    setStep("goal");
    setGoal(null);
    setSelectedService(null);
    setError(null);
    setDraft(null);
    if (initialCampaign) {
      const duplicateGoal: Goal =
        initialCampaign.audience === "new_to_facility"
          ? "welcome"
          : initialCampaign.audience === "new_to_service"
            ? "service_welcome"
            : initialCampaign.audience === "lapsed_to_service"
              ? "win_back"
              : initialCampaign.audience === "earned_referral"
                ? "referral"
                : initialCampaign.audience === "earned_loyalty"
                  ? "loyalty"
                  : initialCampaign.local_time_start ||
                      initialCampaign.eligible_weekdays?.length
                    ? "quiet_hours"
                    : "service";
      setGoal(duplicateGoal);
      setSelectedService(initialCampaign.service);
      setStep("details");
      form.reset({
        ...defaultDetails,
        name: `${initialCampaign.name} copy`,
        discountType: initialCampaign.discount_type,
        discountPercent:
          initialCampaign.discount_percent != null
            ? String(initialCampaign.discount_percent)
            : "",
        discountAmount:
          initialCampaign.discount_amount != null
            ? String(initialCampaign.discount_amount)
            : "",
        spendCap: String(initialCampaign.spend_cap),
        minimumCart:
          initialCampaign.minimum_cart_amount != null
            ? String(initialCampaign.minimum_cart_amount)
            : "",
        maximumDiscount:
          initialCampaign.maximum_discount != null
            ? String(initialCampaign.maximum_discount)
            : "",
        perPlayerLimit: initialCampaign.per_player_limit,
        inactiveForDays: initialCampaign.inactive_for_days ?? 60,
        rewardThreshold: initialCampaign.reward_threshold ?? 5,
        rewardValidForDays: initialCampaign.reward_valid_for_days ?? 30,
        eligibleWeekdays: initialCampaign.eligible_weekdays ?? [],
        startsAt: toZonedDateTimeInput(
          initialCampaign.starts_at,
          context.timezone,
        ),
        endsAt: toZonedDateTimeInput(initialCampaign.ends_at, context.timezone),
        localTimeStart: initialCampaign.local_time_start ?? "",
        localTimeEnd: initialCampaign.local_time_end ?? "",
      });
    } else {
      setStep("goal");
      setGoal(null);
      setSelectedService(null);
      form.reset(defaultDetails);
    }
  }, [context.timezone, form, initialCampaign, open]);

  const audienceCopy =
    audience === "new_to_facility"
      ? "New to this facility — people who have never completed a paid purchase here."
      : audience === "new_to_service"
        ? `New to this service — people who have never completed a paid ${serviceCopy?.noun ?? "purchase"} here.`
        : audience === "lapsed_to_service"
          ? `Returning to this service after at least ${inactiveForDays} days without a completed paid ${serviceCopy?.noun ?? "purchase"}.`
          : audience === "earned_referral"
            ? `Both players earn this reward only after the referred friend completes a verified ${serviceCopy?.noun ?? "service"}. Entering a code alone earns nothing.`
            : audience === "earned_loyalty"
              ? `Earn this reward after ${form.watch("rewardThreshold")} verified ${serviceCopy?.noun ?? "service"} completions.`
              : "All players may use this offer, up to the per-player limit.";
  const previewCopy =
    discountType === "fixed_amount"
      ? `${currency ?? "—"} ${discountAmount || "—"} off a ${serviceCopy?.noun ?? "service"}`
      : `${discountPercent || "—"}% off a ${serviceCopy?.noun ?? "service"}`;

  const chooseGoal = (selected: Goal) => {
    setGoal(selected);
    if (
      selected === "welcome" ||
      selected === "service_welcome" ||
      selected === "win_back"
    ) {
      form.setValue("perPlayerLimit", 1);
    } else if (selected === "quiet_hours") {
      form.setValue("perPlayerLimit", 3);
      form.setValue("eligibleWeekdays", [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
      ]);
    } else if (selected === "limited_time") {
      form.setValue("discountPercent", "15");
      form.setValue("maximumDiscount", "10");
      form.setValue("perPlayerLimit", 1);
    } else if (selected === "repeat_visits") {
      form.setValue("discountPercent", "10");
      form.setValue("maximumDiscount", "10");
      form.setValue("perPlayerLimit", 3);
    } else if (selected === "spend_save") {
      form.setValue("discountType", "fixed_amount");
      form.setValue("discountPercent", "");
      form.setValue("discountAmount", "10");
      form.setValue("minimumCart", "50");
      form.setValue("maximumDiscount", "");
      form.setValue("perPlayerLimit", 3);
    } else if (selected === "referral") {
      form.setValue("perPlayerLimit", 10);
      form.setValue("rewardValidForDays", 30);
    } else if (selected === "loyalty") {
      form.setValue("perPlayerLimit", 3);
      form.setValue("rewardThreshold", 5);
      form.setValue("rewardValidForDays", 30);
    }
    setStep("service");
  };

  const chooseService = (service: PromotionService) => {
    setSelectedService(service);
    const presentation = servicePresentation[service];
    form.setValue(
      "name",
      goal === "welcome"
        ? `New player ${presentation.noun} welcome`
        : goal === "service_welcome"
          ? `First ${presentation.noun} offer`
          : goal === "quiet_hours"
            ? `Quiet-hours ${presentation.noun} special`
            : goal === "limited_time"
              ? `Limited-time ${presentation.noun} special`
              : goal === "repeat_visits"
                ? `Repeat ${presentation.noun} reward`
                : goal === "spend_save"
                  ? `Spend and save on ${presentation.noun}s`
                  : goal === "win_back"
                    ? `Come back for a ${presentation.noun}`
                    : goal === "referral"
                      ? `Refer a friend · ${presentation.noun} reward`
                      : goal === "loyalty"
                        ? `Verified ${presentation.noun} loyalty reward`
                        : presentation.defaultName,
    );
    setStep("details");
  };

  const submit = form.handleSubmit(async (values) => {
    if (!selectedService || !currency || !serviceContext?.currency) {
      setError(
        "Set one pricing currency for this service before publishing a promotion.",
      );
      return;
    }
    const startsAt = fromZonedDateTimeInput(values.startsAt, context.timezone);
    const endsAt = fromZonedDateTimeInput(values.endsAt, context.timezone);
    if (!startsAt || !endsAt || endsAt <= startsAt) {
      form.setError("endsAt", {
        message: `End must be after a valid start in ${context.timezone}.`,
      });
      setError(`Enter a valid schedule in ${context.timezone}.`);
      return;
    }
    if (
      goal === "quiet_hours" &&
      (values.eligibleWeekdays.length === 0 ||
        !values.localTimeStart ||
        !values.localTimeEnd)
    ) {
      if (values.eligibleWeekdays.length === 0) {
        form.setError("eligibleWeekdays", {
          message: "Choose at least one quiet-hours weekday.",
        });
      }
      if (!values.localTimeStart) {
        form.setError("localTimeStart", {
          message: "Choose when quiet hours begin.",
        });
      }
      if (!values.localTimeEnd) {
        form.setError("localTimeEnd", {
          message: "Choose when quiet hours end.",
        });
      }
      setError(
        "Quiet-hours promotions require weekdays and a bounded time window.",
      );
      return;
    }
    if (
      (goal === "referral" || goal === "loyalty") &&
      values.discountType === "percentage" &&
      !values.maximumDiscount
    ) {
      form.setError("maximumDiscount", {
        message: "Set the maximum value of each earned reward.",
      });
      setError("Earned percentage rewards require a maximum liability.");
      return;
    }
    setError(null);
    setDraft({
      name: values.name,
      service: selectedService,
      offering_id: null,
      audience,
      inactive_for_days:
        audience === "lapsed_to_service" ? values.inactiveForDays : null,
      reward_threshold:
        audience === "earned_loyalty" ? values.rewardThreshold : null,
      reward_valid_for_days:
        audience === "earned_referral" || audience === "earned_loyalty"
          ? values.rewardValidForDays
          : null,
      discount_type: values.discountType,
      discount_percent:
        values.discountType === "percentage"
          ? asRatePercent(values.discountPercent)
          : null,
      discount_amount:
        values.discountType === "fixed_amount"
          ? asMoney(values.discountAmount)
          : null,
      maximum_discount:
        values.discountType === "percentage" && values.maximumDiscount
          ? asMoney(values.maximumDiscount)
          : null,
      minimum_cart_amount: values.minimumCart
        ? asMoney(values.minimumCart)
        : null,
      starts_at: startsAt,
      ends_at: endsAt,
      spend_cap: asMoney(values.spendCap)!,
      per_player_limit:
        audience === "all_players" ||
        audience === "earned_referral" ||
        audience === "earned_loyalty"
          ? values.perPlayerLimit
          : 1,
      eligible_weekdays:
        values.eligibleWeekdays.length > 0 ? values.eligibleWeekdays : null,
      local_time_start: values.localTimeStart || null,
      local_time_end: values.localTimeEnd || null,
      currency: serviceContext.currency,
      public: true,
    });
    setStep("review");
  });

  const publish = async () => {
    if (!draft) return;
    setError(null);
    try {
      await onCreate(draft);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The promotion could not be published.",
      );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={compact}
    >
      <DialogTitle>
        {step === "goal"
          ? "Choose a promotion goal"
          : step === "service"
            ? "Choose a service"
            : step === "details"
              ? "Build your promotion"
              : "Review and publish"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {selectedService ? (
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              useFlexGap
              sx={{ flexWrap: "wrap" }}
            >
              <Chip
                label={`${currency ?? "Currency not configured"} · facility pricing currency`}
              />
              <Chip label={`${context.timezone} · facility local time`} />
              {serviceContext?.platform_fee_per_redemption != null &&
              serviceContext.currency ? (
                <Chip
                  label={`${formatMoney(serviceContext.platform_fee_per_redemption, serviceContext.currency)} BW fee per redemption`}
                />
              ) : null}
            </Stack>
          ) : null}
          {error ? (
            <Typography color="error" role="alert" aria-live="polite">
              {error}
            </Typography>
          ) : null}
          {step === "goal" ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
                gap: 2,
              }}
            >
              {goals.map((item) => (
                <Button
                  key={item.id}
                  variant="outlined"
                  onClick={() => chooseGoal(item.id)}
                  sx={{
                    p: 2,
                    textAlign: "left",
                    justifyContent: "flex-start",
                    alignItems: "flex-start",
                  }}
                >
                  <Stack spacing={1}>
                    {item.icon}
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {item.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {item.description}
                    </Typography>
                  </Stack>
                </Button>
              ))}
            </Box>
          ) : null}
          {step === "service" ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                gap: 1.5,
              }}
            >
              {services.map((service) => {
                const authoring = campaignServiceContext(context, service);
                const audienceSupported = !(
                  service === "coaching" && goal === "welcome"
                );
                const enabled =
                  authoring?.currency != null && audienceSupported;
                return (
                  <Button
                    key={service}
                    variant="outlined"
                    disabled={!enabled}
                    onClick={() => enabled && chooseService(service)}
                    sx={{ minHeight: 72, justifyContent: "space-between" }}
                  >
                    <span>{servicePresentation[service].label}</span>
                    {!enabled ? (
                      <Chip
                        label={
                          audienceSupported
                            ? "Setup needed"
                            : "Choose first coaching visit"
                        }
                        size="small"
                      />
                    ) : null}
                  </Button>
                );
              })}
            </Box>
          ) : null}
          {step === "details" ? (
            <Box
              component="form"
              id="promotion-studio-form"
              onSubmit={submit}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 340px" },
                gap: 3,
              }}
            >
              <Stack spacing={2}>
                <Paper
                  component="section"
                  aria-label="Audience"
                  variant="outlined"
                  sx={{ p: 2 }}
                >
                  <Typography variant="overline">Audience</Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {audience === "new_to_facility"
                      ? "New to this facility"
                      : audience === "new_to_service"
                        ? "New to this service"
                        : audience === "lapsed_to_service"
                          ? "Returning after time away"
                          : audience === "earned_referral"
                            ? "Verified friend referrals"
                            : audience === "earned_loyalty"
                              ? "Verified service milestones"
                              : "All players"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {audienceCopy}
                  </Typography>
                  {selectedService === "classes" ? (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
                      Scope: All classes &amp; clinics at this facility
                    </Typography>
                  ) : null}
                </Paper>
                <TextField
                  label="Promotion name"
                  {...form.register("name")}
                  error={Boolean(form.formState.errors.name)}
                  helperText={form.formState.errors.name?.message}
                />
                <TextField
                  select
                  label="Offer type"
                  defaultValue="percentage"
                  {...form.register("discountType")}
                >
                  <MenuItem value="percentage">Percentage off</MenuItem>
                  <MenuItem value="fixed_amount">Fixed amount off</MenuItem>
                </TextField>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  {discountType === "percentage" ? (
                    <TextField
                      label="Discount (%)"
                      {...form.register("discountPercent")}
                      error={Boolean(form.formState.errors.discountPercent)}
                      helperText={
                        form.formState.errors.discountPercent?.message
                      }
                    />
                  ) : (
                    <TextField
                      label={`Discount amount (${currency ?? "—"})`}
                      {...form.register("discountAmount")}
                      error={Boolean(form.formState.errors.discountAmount)}
                      helperText={form.formState.errors.discountAmount?.message}
                    />
                  )}
                  <TextField
                    label={`Budget cap (${currency ?? "—"})`}
                    {...form.register("spendCap")}
                    error={Boolean(form.formState.errors.spendCap)}
                    helperText={form.formState.errors.spendCap?.message}
                  />
                  <TextField
                    label={`Minimum booking total (${currency ?? "—"})`}
                    {...form.register("minimumCart")}
                    error={Boolean(form.formState.errors.minimumCart)}
                    helperText={form.formState.errors.minimumCart?.message}
                  />
                  {discountType === "percentage" ? (
                    <TextField
                      label={`Maximum discount (${currency ?? "—"})`}
                      {...form.register("maximumDiscount")}
                      error={Boolean(form.formState.errors.maximumDiscount)}
                      helperText={
                        form.formState.errors.maximumDiscount?.message
                      }
                    />
                  ) : null}
                  <TextField
                    label="Uses per player"
                    type="number"
                    {...form.register("perPlayerLimit", {
                      valueAsNumber: true,
                    })}
                    disabled={
                      audience !== "all_players" &&
                      audience !== "earned_referral" &&
                      audience !== "earned_loyalty"
                    }
                    error={Boolean(form.formState.errors.perPlayerLimit)}
                    helperText={form.formState.errors.perPlayerLimit?.message}
                  />
                  {audience === "lapsed_to_service" ? (
                    <TextField
                      label="Days without this service"
                      type="number"
                      {...form.register("inactiveForDays", {
                        valueAsNumber: true,
                      })}
                      error={Boolean(form.formState.errors.inactiveForDays)}
                      helperText={
                        form.formState.errors.inactiveForDays?.message ??
                        "30–365 days, based on verified service history."
                      }
                    />
                  ) : null}
                  {audience === "earned_loyalty" ? (
                    <TextField
                      label="Verified services per reward"
                      type="number"
                      {...form.register("rewardThreshold", {
                        valueAsNumber: true,
                      })}
                      error={Boolean(form.formState.errors.rewardThreshold)}
                      helperText={
                        form.formState.errors.rewardThreshold?.message ??
                        "2–50 completed, canonical service fulfillments."
                      }
                    />
                  ) : null}
                  {audience === "earned_referral" ||
                  audience === "earned_loyalty" ? (
                    <TextField
                      label="Reward valid for (days)"
                      type="number"
                      {...form.register("rewardValidForDays", {
                        valueAsNumber: true,
                      })}
                      error={Boolean(form.formState.errors.rewardValidForDays)}
                      helperText={
                        form.formState.errors.rewardValidForDays?.message ??
                        "Existing earned rewards remain usable during this period."
                      }
                    />
                  ) : null}
                </Box>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 1.5,
                  }}
                >
                  <TextField
                    label="Starts at"
                    type="datetime-local"
                    {...form.register("startsAt")}
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={Boolean(form.formState.errors.startsAt)}
                    helperText={form.formState.errors.startsAt?.message}
                  />
                  <TextField
                    label="Ends at"
                    type="datetime-local"
                    {...form.register("endsAt")}
                    slotProps={{ inputLabel: { shrink: true } }}
                    error={Boolean(form.formState.errors.endsAt)}
                    helperText={form.formState.errors.endsAt?.message}
                  />
                  {goal === "quiet_hours" ? (
                    <>
                      <FormGroup
                        row
                        aria-label="Eligible weekdays"
                        sx={{ gridColumn: "1 / -1" }}
                      >
                        {weekdays.map(([value, label]) => (
                          <FormControlLabel
                            key={value}
                            control={
                              <Checkbox
                                value={value}
                                {...form.register("eligibleWeekdays")}
                              />
                            }
                            label={label}
                          />
                        ))}
                      </FormGroup>
                      {form.formState.errors.eligibleWeekdays ? (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ gridColumn: "1 / -1" }}
                        >
                          {form.formState.errors.eligibleWeekdays.message}
                        </Typography>
                      ) : null}
                      <TextField
                        label="Eligible from"
                        type="time"
                        {...form.register("localTimeStart")}
                        slotProps={{ inputLabel: { shrink: true } }}
                        error={Boolean(form.formState.errors.localTimeStart)}
                        helperText={
                          form.formState.errors.localTimeStart?.message
                        }
                      />
                      <TextField
                        label="Eligible until"
                        type="time"
                        {...form.register("localTimeEnd")}
                        slotProps={{ inputLabel: { shrink: true } }}
                        error={Boolean(form.formState.errors.localTimeEnd)}
                        helperText={form.formState.errors.localTimeEnd?.message}
                      />
                    </>
                  ) : null}
                </Box>
              </Stack>
              <Paper
                component="section"
                aria-label="Player preview"
                variant="outlined"
                sx={{
                  p: 2,
                  alignSelf: "start",
                  position: { md: "sticky" },
                  top: { md: 16 },
                }}
              >
                <Stack spacing={1.25}>
                  <Typography variant="overline">Player preview</Typography>
                  <Chip
                    label={`${serviceCopy?.label ?? "Service"} offer`}
                    color="primary"
                    size="small"
                    sx={{ alignSelf: "flex-start" }}
                  />
                  <Typography variant="h6">
                    {form.watch("name") ||
                      `Special ${serviceCopy?.noun ?? "service"} offer`}
                  </Typography>
                  <Typography variant="body2">{previewCopy}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {context.facility_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {audienceCopy}
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          ) : null}
          {step === "review" && draft ? (
            <Box
              component="section"
              aria-label="Promotion review"
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 340px" },
                gap: 3,
              }}
            >
              <Stack spacing={2}>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline">
                    Audience and service
                  </Typography>
                  <Typography sx={{ fontWeight: 800 }}>
                    {audience === "new_to_facility"
                      ? "New to this facility"
                      : audience === "new_to_service"
                        ? "New to this service"
                        : audience === "lapsed_to_service"
                          ? "Returning after time away"
                          : audience === "earned_referral"
                            ? "Verified friend referrals"
                            : audience === "earned_loyalty"
                              ? "Verified service milestones"
                              : "All players"}{" "}
                    · {serviceCopy?.label ?? "Service"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {audienceCopy}
                  </Typography>
                  {selectedService === "classes" ? (
                    <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
                      Applies to all classes &amp; clinics at this facility.
                    </Typography>
                  ) : null}
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline">Offer terms</Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {draft.discount_type === "fixed_amount"
                        ? `${formatMoney(draft.discount_amount, draft.currency)} off`
                        : `${formatRatePercent(draft.discount_percent, 2)} off`}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Minimum booking:{" "}
                      {draft.minimum_cart_amount != null
                        ? formatMoney(draft.minimum_cart_amount, draft.currency)
                        : "None"}{" "}
                      · Maximum discount:{" "}
                      {draft.maximum_discount != null
                        ? formatMoney(draft.maximum_discount, draft.currency)
                        : "None"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Uses per player: {draft.per_player_limit}
                    </Typography>
                    {draft.audience === "earned_loyalty" ? (
                      <Typography variant="body2" color="text.secondary">
                        Earn after {draft.reward_threshold} verified services ·
                        reward valid {draft.reward_valid_for_days} days
                      </Typography>
                    ) : null}
                    {draft.audience === "earned_referral" ? (
                      <Typography variant="body2" color="text.secondary">
                        Both players qualify after the friend&apos;s verified
                        fulfillment · reward valid {draft.reward_valid_for_days}{" "}
                        days
                      </Typography>
                    ) : null}
                    <Typography variant="body2" color="text.secondary">
                      Eligible days:{" "}
                      {draft.eligible_weekdays?.join(", ") ?? "Every day"}
                      {draft.local_time_start && draft.local_time_end
                        ? ` · ${draft.local_time_start}–${draft.local_time_end} facility local time`
                        : " · Any time"}
                    </Typography>
                  </Stack>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline">
                    Merchant commitment
                  </Typography>
                  <Typography variant="body2">
                    Budget cap {formatMoney(draft.spend_cap, draft.currency)} ·
                    BW fee{" "}
                    {serviceContext?.platform_fee_per_redemption != null
                      ? formatMoney(
                          serviceContext.platform_fee_per_redemption,
                          draft.currency,
                        )
                      : "Unavailable"}{" "}
                    per successful redemption.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    The total budget cap includes both customer discounts and BW
                    promotion fees. They remain separate ledger components.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1 }}
                  >
                    {draft.audience === "earned_referral" ||
                    draft.audience === "earned_loyalty"
                      ? "Pausing or ending stops new earning. Existing reward grants remain redeemable until their recorded expiry; receipts, refunds, and recovery keep their original terms."
                      : "Pausing or ending stops new redemptions. Existing bookings, receipts, refunds, and recovery keep their original terms."}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline">Schedule</Typography>
                  <Typography variant="body2">
                    {new Intl.DateTimeFormat("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                      timeZone: context.timezone,
                    }).format(new Date(draft.starts_at))}{" "}
                    –{" "}
                    {new Intl.DateTimeFormat("en-US", {
                      dateStyle: "medium",
                      timeStyle: "short",
                      timeZone: context.timezone,
                    }).format(new Date(draft.ends_at))}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {context.timezone} facility local time
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="overline">Player discovery</Typography>
                  <Typography variant="body2">
                    {serviceContext?.service_ready
                      ? "This promotion will appear as a facility-published offer in Player Home, Play, search, the facility page, and eligible checkout flows."
                      : `This promotion will appear in Player discovery once setup for ${serviceCopy?.setupName ?? "the service"} is complete and its schedule begins. You can prepare it now without advertising an unavailable service.`}
                  </Typography>
                </Paper>
              </Stack>
              <Paper
                component="section"
                aria-label="Player preview"
                variant="outlined"
                sx={{
                  p: 2,
                  alignSelf: "start",
                  position: { md: "sticky" },
                  top: { md: 16 },
                }}
              >
                <Stack spacing={1.25}>
                  <Typography variant="overline">Player preview</Typography>
                  <Chip
                    label={`${serviceCopy?.label ?? "Service"} offer`}
                    color="primary"
                    size="small"
                    sx={{ alignSelf: "flex-start" }}
                  />
                  <Typography variant="h6">{draft.name}</Typography>
                  <Typography variant="body2">{previewCopy}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {context.facility_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {audienceCopy}
                  </Typography>
                </Stack>
              </Paper>
            </Box>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {step !== "goal" ? (
          <Button
            startIcon={<ArrowBackRoundedIcon />}
            onClick={() =>
              setStep(
                step === "review"
                  ? "details"
                  : step === "details"
                    ? "service"
                    : "goal",
              )
            }
            disabled={busy}
          >
            Back
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        {step === "details" ? (
          <Button
            variant="contained"
            type="submit"
            form="promotion-studio-form"
            disabled={busy || !currency}
          >
            Review and publish
          </Button>
        ) : null}
        {step === "review" ? (
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => void publish()}
          >
            {busy ? "Publishing…" : "Publish promotion"}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
